import React, { useEffect, useRef, useState } from "react";
import "./DrugAssignment.css";
import Loader from "../../components/Loader";
import { Box, Divider, Grid2, TextField } from "@mui/material";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import useNavigationGuard from "../../hooks/useNavigationGuard";
import { Controller, useForm } from "react-hook-form";
import { InfoOutlined } from "@mui/icons-material";
import CustomSwitch from "../../components/CustomSwitch/CustomSwitch";
import { CreateHandleUnsaveNavigation } from "../../utilities/handleUnsaveNavigation";
import {
  fetchOnLoadDataService,
  getNumberOfDrugsService,
  saveBulkDataService,
} from "../../services/drugAssignmentService";
import { notifyError, notifySuccess } from "../../utilities/helper";
import DrugConfirmation from "./DrugConfirmation/DrugConfirmation";
import SearchableSelect from "../../components/SearchableSelect";

const DrugAssignmentBatchRelease = () => {
  const [load, setLoad] = useState(false);
  const [loadData, setLoadData] = useState({
    showLabelGrpData: false,
    unReleeaseDrugData: [],
    drugTypeData: [],
    newLotData: [],
    currentLotData: [],
    newDepotData: [],
    currentLabelGrpData: [],
    newLabelGrpData: [],
    currentDepotData: [],
    protocolData: [],
  });
  const [numOfDrugs, setNumOfDrugs] = useState("");
  const [showConfirmPage, setShowConfirmPage] = useState(false);
  const [confirmationData, setConfirmationData] = useState({
    drugName: "",
    seqRange: "",
    kitCount: "",
    lot: "",
    depotName: "",
    labelGrp: "",
    protocols: "",
  });
  const navigate = useNavigate();

  const access_token = localStorage.getItem("access_token");
  const roleId = localStorage.getItem("roleId");
  const handleUnsaveNavigation = CreateHandleUnsaveNavigation();
  const prevDrugTypeRef = useRef();

  const {
    showLabelGrpData,
    currentDepotData,
    currentLabelGrpData,
    currentLotData,
    drugTypeData,
    newDepotData,
    newLabelGrpData,
    newLotData,
    unReleeaseDrugData,
    protocolData,
  } = loadData;

  const createDrugAsignment = yup.object().shape({
    drugType: yup.string().required("Drug Type is required"),
    newLot: yup.string().required("New Lot is required"),
    minSequenceNumber: yup
      .string()
      .matches(/^\d+(\.\d+)?$/, "Min Sequence Number is not valid.")
      .required("Min Sequence Number is required")
      .max(999999999, "Min Sequence Number should be less than 999999999"),
    maxSequenceNumber: yup
      .string()
      .matches(/^\d+(\.\d+)?$/, "Max Sequence Number is not valid.")
      .required("Max Sequence Number  is required")
      .max(999999999, "Max Sequence Number should be less than 999999999"),
    currentLot: yup.string(),
    // .required("New Lot is required")
    newDepot: yup.string().required("New Depot is required"),
    currentLabelGroup: yup.string(),
    // .required("Current Label Group is required")
    newLabelGroup: yup.string().required("New Label Group is required"),
    currentDepot: yup.string(),
    // .required("Current Depot is required")
    noChange: yup.boolean(),
    legacy: yup.boolean(),
    releaseIrt: yup.boolean(),
  });

  const {
    register,
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    resolver: yupResolver(createDrugAsignment),
    defaultValues: {
      drugType: "",
      newLot: "",
      minSequenceNumber: "",
      maxSequenceNumber: "",
      notes: "",
      currentLot: "",
      newDepot: "",
      currentDepot: "",
      currentLabelGroup: "",
      legacy: false,
      newLabelGroup: "",
      noChange: false,
      releaseIrt: false,
    },
  });

  useNavigationGuard(isDirty);

  useEffect(() => {
    getOnLoadData();
  }, []);

  useEffect(() => {
    if (drugTypeData && drugTypeData?.length > 0) {
      getNumOfDrugData(drugTypeData[0]?.id);
    }
  }, [drugTypeData]);

  const values = watch();

  useEffect(() => {
    if (drugTypeData && drugTypeData?.length > 0) {
      setTimeout(() => {
        getNumOfDrugData(
          values?.drugType,
          values?.minSequenceNumber,
          values?.maxSequenceNumber,
          values?.currentLot,
          values?.currentDepot,
          values?.currentLabelGroup
        );
      }, 200);
    }
  }, [
    values?.drugType,
    values?.minSequenceNumber,
    values?.maxSequenceNumber,
    values?.currentLot,
    values?.currentDepot,
    values?.currentLabelGroup,
    drugTypeData,
  ]);

  useEffect(() => {
    if (prevDrugTypeRef.current !== values?.drugType) {
      prevDrugTypeRef.current = values?.drugType;

      reset({
        maxSequenceNumber: "",
        minSequenceNumber: "",
        newLot: "",
        newDepot: "",
        newLabelGroup: "",
        currentLot: "",
        currentDepot: "",
        currentLabelGroup: "",
        legacy: false,
        releaseIrt: false,
        noChange: false,
        drugType: values?.drugType, // preserve this
      });
    }
  }, [values?.drugType]);

  const getOnLoadData = async () => {
    try {
      setLoad(true);
      const res = await fetchOnLoadDataService(access_token);
      if (res?.status == 200) {
        setLoad(false);
        const data = res?.data?.result;
        setLoadData({
          showLabelGrpData: data?.useLabelGroups,
          unReleeaseDrugData: data?.unreleasedDrugs,
          drugTypeData: data?.drugTypes,
          // currentDepotData: data?.depots,
          // currentLabelGrpData: data?.labelGroups,
          // currentLotData: data?.lots,
          newDepotData: data?.depotsWithUnassign,
          newLabelGrpData: data?.labelGroupsWithUnassign,
          newLotData: data?.lotsWithUnassign,
          protocolData: data?.protocols,
        });
        reset({
          drugType: data.drugTypes[0]?.id,
        });
      }
    } catch (err) {
      console.log(err.message);
      setLoad(false);
      if (err?.status == 401) {
        navigate("/unAuthorize");
      }
    }
  };

  const getNumOfDrugData = async (
    drugId,
    minSeq = 0,
    maxSex = 0,
    lotId = null,
    depotId = null,
    labelGroupId = null
  ) => {
    try {
      const data = {
        minSequenceNumber: minSeq || 0,
        maxSequenceNumber: maxSex || 0,
        lotId: lotId || "00000000-0000-0000-0000-000000000000",
        drugId: drugId,
        depotId: depotId || "00000000-0000-0000-0000-000000000000",
        labelGroupId: labelGroupId || "00000000-0000-0000-0000-000000000000",
      };
      const res = await getNumberOfDrugsService(access_token, data);
      setNumOfDrugs(res?.data?.result?.count);
      setLoadData((prev) => ({
        ...prev,
        currentDepotData: res?.data?.result?.currentDepots,
        currentLabelGrpData: res?.data?.result?.currentLabelGroups,
        currentLotData: res?.data?.result?.currentLots,
      }));
    } catch (err) {
      console.log(err?.message);
    }
  };

  const updateDrugAssignment = async (data) => {
    const newData = {
      bulkUpdateLotId: data?.newLot,
      bulkUpdateDepotId: data?.newDepot,
      bulkUpdateQuantity: numOfDrugs,
      bulkUpdateLabelGroupId: data?.newLabelGrpData,
      currentFilter: {
        minSequenceNumber: data?.minSequenceNumber,
        maxSequenceNumber: data?.maxSequenceNumber,
        lotId:
          data?.currentLot == ""
            ? "00000000-0000-0000-0000-000000000000"
            : data?.currentLot,
        drugId: data?.drugType,
        depotId:
          data?.currentDepot == ""
            ? "00000000-0000-0000-0000-000000000000"
            : data?.currentDepot,
        drugStatusIds: [],
        minKitNumber: "",
        maxKitNumber: "",
        siteId: "00000000-0000-0000-0000-000000000000",
        labelGroupId:
          data?.currentLabelGroup == ""
            ? "00000000-0000-0000-0000-000000000000"
            : data?.currentLabelGroup,
        labelGroup: "",
        protocolId: "00000000-0000-0000-0000-000000000000",
      },
      protocols: [
        {
          id: "a5ec17cb-d24c-46f0-a69b-54a32f287d36",
          name: "Legacy",
          selected: data?.legacy,
        },
        {
          id: "52daa432-459d-45a3-b3e9-d234d9aa5ef7",
          name: "Release 3.1 IRT",
          selected: data?.releaseIrt,
        },
        {
          id: "00000000-0000-0000-0000-000000000000",
          name: "No Change",
          selected: data?.noChange,
        },
      ],
      userId: roleId,
    };

    try {
      setLoad(true);
      const res = await saveBulkDataService(access_token, newData);
      if (res?.data?.status == 401) {
        setLoad(false);
        notifyError(res?.data?.message);
        // reset();
      } else {
        const data = res?.data?.result;
        setLoad(false);
        notifySuccess("Bulk Drug Assignment Saved Successfully");
        reset();
        setShowConfirmPage(true);
        setConfirmationData({
          depotName: data?.depotName,
          drugName: data?.drugType,
          seqRange: data?.sequenceRange,
          kitCount: data?.kitCount,
          lot: data?.lotName,
          labelGrp: data?.labelGroupName,
          protocols: data?.protocols,
        });
      }
    } catch (err) {
      setLoad(false);
      setShowConfirmPage(false);
      console.log(err.message);
      notifyError(err?.message);
      if (err?.status == 401) {
        navigate("/unAuthorize");
      }
    }
  };

  const minSequenceRegister = register("minSequenceNumber");
  const maxSequenceRegister = register("maxSequenceNumber");

  return (
    <>
      {showConfirmPage ? (
        <DrugConfirmation
          setShowConfirm={setShowConfirmPage}
          data={confirmationData}
        />
      ) : (
        <>
          {load ? (
            <Loader />
          ) : (
            <div className="content-body">
              <p className="bread-crumbs-link-container">
                <button
                  className="bread-crumbs-btn"
                  onClick={(e) =>
                    handleUnsaveNavigation(e, isDirty, "", navigate)
                  }
                >
                  Home
                </button>{" "}
                |{" "}
                <button
                  className="bread-crumbs-btn"
                  onClick={(e) => {
                    handleUnsaveNavigation(
                      e,
                      isDirty,
                      "drug-assignment-batch-release",
                      navigate
                    );
                    setShowConfirmPage(false);
                  }}
                >
                  Drug Assignment - Batch Release
                </button>
                -{" "}
                <b style={{ color: "gray" }}>
                  {localStorage?.getItem("studyName")}
                </b>
              </p>
              <Grid2
                container
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: "100%", margin: "0 auto" }}
              >
                <Grid2 item md={12}>
                  <p className="page-heading">
                    Drug Assignment - Batch Release
                  </p>
                  <Divider />
                  <p className="page-header">
                    <InfoOutlined className="info-icon" />
                    This page will select drug-kits in "Not Released" and "Not
                    Assigned" status and create a Batch Release record which
                    later needs to be approved in "Batch Release Approval Page".
                    Same user can NOT approve the batch release they create,
                    they can only view and approve the releases of other users
                    (Depot managers)
                  </p>
                  <p className="page-sub-header">
                    <InfoOutlined className="info-icon" />
                    If you want to make an update to individual kits/units,
                    please use the{" "}
                    <button
                      className="bread-crumbs-btn"
                      onClick={(e) =>
                        handleUnsaveNavigation(
                          e,
                          isDirty,
                          "drug-assignment-individual-release",
                          navigate
                        )
                      }
                      style={{ fontSize: "14px" }}
                    >
                      Individual update function
                    </button>
                  </p>
                </Grid2>
              </Grid2>
              <div className="bulk-drug-table-container">
                <div className="drug-heading">
                  Unrelease Drug Sequence Range Information
                </div>
                <table className="bulk-drug-table">
                  <thead>
                    <tr>
                      <th>Drug Name</th>
                      <th>Min Range</th>
                      <th>Max Range</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unReleeaseDrugData &&
                      unReleeaseDrugData?.map((item, index) => (
                        <tr key={index}>
                          <td>{item?.drugName}</td>
                          <td>{item?.sequenceRangeLow}</td>
                          <td>{item?.sequenceRangeHigh}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              <Box
                component="form"
                autoComplete="off"
                sx={{ height: "auto", width: "100%" }}
                onSubmit={handleSubmit(updateDrugAssignment)}
              >
                <Grid2
                  container
                  alignItems="center"
                  justifyContent={"space-between"}
                  sx={{ width: "100%", margin: "0 auto", marginTop: "20px" }}
                  gap={1}
                >
                  <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                    <div>
                      <p className="labelStyles">
                        Drug Type
                        <span>*</span>
                      </p>
                      <SearchableSelect
                        control={control}
                        data={drugTypeData}
                        name="drugType"
                        noDefault={true}
                        optionKey="id"
                        optionLabel="name"
                        ref={prevDrugTypeRef}
                      />
                    </div>
                    {errors.drugType && (
                      <div className="error-container">
                        {errors.drugType.message}
                      </div>
                    )}
                  </Grid2>

                  <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                    <div>
                      {numOfDrugs !== "" && (
                        <div className="drug-count-container">
                          {numOfDrugs} Drug{" "}
                          {numOfDrugs === 1 ? "item" : "items"} Found
                        </div>
                      )}
                    </div>
                  </Grid2>

                  <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                    <div>
                      <p className="labelStyles">
                        Min Sequence Number
                        <span>*</span>
                      </p>
                      <TextField
                        fullWidth
                        size="small"
                        type="text"
                        {...minSequenceRegister}
                        onChange={(e) => {
                          let value = e.target.value.replace(/\D/g, ""); // Remove non-digits
                          value = value.replace(/^0+/, ""); // Remove leading zeros
                          value = value.slice(0, 9); // Trim to 9 digits max
                          e.target.value = value;
                          minSequenceRegister.onChange(e);
                        }}
                      />
                    </div>
                    {errors.minSequenceNumber && (
                      <div className="error-container">
                        {errors.minSequenceNumber.message}
                      </div>
                    )}
                  </Grid2>

                  <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                    <div>
                      <p className="labelStyles">
                        New Lot
                        <span>*</span>
                      </p>
                      <SearchableSelect
                        control={control}
                        data={newLotData}
                        name="newLot"
                      />
                    </div>
                    {errors.newLot && (
                      <div className="error-container">
                        {errors.newLot.message}
                      </div>
                    )}
                  </Grid2>

                  <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                    <div>
                      <p className="labelStyles">
                        Max Sequence Number
                        <span>*</span>
                      </p>
                      <TextField
                        fullWidth
                        size="small"
                        type={"number"}
                        {...maxSequenceRegister}
                        onChange={(e) => {
                          let value = e.target.value.replace(/\D/g, ""); // Remove non-digits
                          if (value !== "0") value = value.replace(/^0+/, ""); // Remove leading zeros
                          value = value.slice(0, 9); // Trim to 9 digits max
                          e.target.value = value;
                          maxSequenceRegister.onChange(e);
                        }}
                      />
                    </div>
                    {errors.maxSequenceNumber && (
                      <div className="error-container">
                        {errors.maxSequenceNumber.message}
                      </div>
                    )}
                  </Grid2>

                  <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                    <div>
                      <p className="labelStyles">
                        New Depot
                        <span>*</span>
                      </p>
                      <SearchableSelect
                        control={control}
                        data={newDepotData}
                        name="newDepot"
                      />
                    </div>
                    {errors.newDepot && (
                      <div className="error-container">
                        {errors.newDepot.message}
                      </div>
                    )}
                  </Grid2>

                  <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                    <div>
                      <p className="labelStyles">
                        Current Lot
                        {/* <span>*</span> */}
                      </p>
                      <SearchableSelect
                        control={control}
                        data={currentLotData}
                        name="currentLot"
                      />
                    </div>
                    {errors.currentLot && (
                      <div className="error-container">
                        {errors.currentLot.message}
                      </div>
                    )}
                  </Grid2>
                  {showLabelGrpData && (
                    <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                      <div>
                        <p className="labelStyles">
                          New Label Group
                          <span>*</span>
                        </p>
                        <SearchableSelect
                          control={control}
                          data={newLabelGrpData}
                          name="newLabelGroup"
                        />
                      </div>
                      {errors.newLabelGroup && (
                        <div className="error-container">
                          {errors.newLabelGroup.message}
                        </div>
                      )}
                    </Grid2>
                  )}

                  {showLabelGrpData && (
                    <>
                      <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                        <div>
                          <p className="labelStyles">
                            Current Label Group
                            {/* <span>*</span> */}
                          </p>
                          <SearchableSelect
                            control={control}
                            data={currentLabelGrpData}
                            name="currentLabelGroup"
                          />
                        </div>
                        {errors.currentLabelGroup && (
                          <div className="error-container">
                            {errors.currentLabelGroup.message}
                          </div>
                        )}
                      </Grid2>
                    </>
                  )}

                  <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                    <div>
                      <h6
                        className="labelStyles"
                        style={{ marginBottom: "0px" }}
                      >
                        New Protocol Availibility
                      </h6>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        {protocolData &&
                          protocolData?.map((item, index) => (
                            <div key={index}>
                              <p className="labelStyles">
                                {item?.value == "" ? "No Change" : item?.value}
                              </p>
                              <Controller
                                name={
                                  item?.value === ""
                                    ? "noChange"
                                    : item?.value === "Release 3.1 IRT"
                                    ? "releaseIrt"
                                    : "legacy"
                                }
                                control={control}
                                defaultValue={false}
                                render={({ field }) => (
                                  <CustomSwitch
                                    isSwitch={field.value}
                                    setSwitch={(val) => field.onChange(val)}
                                  />
                                )}
                              />
                            </div>
                          ))}
                      </div>
                    </div>
                  </Grid2>

                  <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                    <div>
                      <p className="labelStyles">
                        Current Depot
                        {/* <span>*</span> */}
                      </p>
                      <SearchableSelect
                        control={control}
                        data={currentDepotData}
                        name="currentDepot"
                      />
                    </div>
                    {errors.currentDepot && (
                      <div className="error-container">
                        {errors.currentDepot.message}
                      </div>
                    )}
                  </Grid2>

                  {/* <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                  <div>
                    <p className="labelStyles">Legacy</p>
                    <Controller
                      name="legacy"
                      control={control}
                      defaultValue={false}
                      render={({ field }) => (
                        <CustomSwitch
                          isSwitch={field.value}
                          setSwitch={(val) => field.onChange(val)}
                        />
                      )}
                    />
                  </div>
                </Grid2>

                <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                  <div>
                    <p className="labelStyles">Release 3.1 IRT</p>
                    <Controller
                      name="releaseIrt"
                      control={control}
                      defaultValue={false}
                      render={({ field }) => (
                        <CustomSwitch
                          isSwitch={field.value}
                          setSwitch={(val) => field.onChange(val)}
                        />
                      )}
                    />
                  </div>
                </Grid2> */}
                </Grid2>

                <Grid2
                  alignItems={"end"}
                  justifyContent="end"
                  container
                  gap={3}
                  sx={{ marginTop: "2%" }}
                >
                  <Grid2 item md={6}>
                    <button
                      type="button"
                      className="create-btn-outlined"
                      onClick={(e) => {
                        e.preventDefault();
                        handleUnsaveNavigation(e, isDirty, "", navigate);
                      }}
                    >
                      Cancel
                    </button>
                  </Grid2>
                  <Grid2 item md={6}>
                    <button type="submit" className="create-btn">
                      Save
                    </button>
                  </Grid2>
                </Grid2>
              </Box>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default DrugAssignmentBatchRelease;
